/**
 * MessagesTab.js
 * ===============
 * Communication hub for property users
 * Shows conversations and allows messaging
 */

import React, { useState, useEffect, useRef } from "react";
import {
  MessageCircle,
  Plus,
  Send,
  Image,
  Video,
  Mic,
  X,
  Camera,
  Paperclip,
  Check,
  CheckCheck
} from "lucide-react";
import { useAuth } from "../context/AuthContext.jsx";

// Current user ID (in a real app, this would come from auth)
const CURRENT_USER_ID = "user-owner-1";

export default function MessagesTab({ property }) {
  const { getToken } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewConversationModal, setShowNewConversationModal] = useState(false);
  const [propertyUsers, setPropertyUsers] = useState([]);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const propertyId = property?.id;

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch conversations
  useEffect(() => {
    if (!propertyId) return;

    async function fetchConversations() {
      try {
        setLoading(true);
        const res = await fetch(
          `/api/properties/${propertyId}/conversations?userId=${CURRENT_USER_ID}`
        );
        if (!res.ok) throw new Error("Failed to load conversations");
        const data = await res.json();
        setConversations(data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchConversations();
  }, [propertyId]);

  // Fetch property users for new conversation
  useEffect(() => {
    if (!propertyId) return;

    async function fetchUsers() {
      try {
        const res = await fetch(`/api/properties/${propertyId}/users`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        setPropertyUsers(data);
      } catch (err) {
        console.error(err);
      }
    }

    fetchUsers();
  }, [propertyId]);

  // Fetch messages for selected conversation
  useEffect(() => {
    if (!selectedConversation) {
      setMessages([]);
      return;
    }

    async function fetchMessages() {
      try {
        const res = await fetch(
          `/api/conversations/${selectedConversation.id}/messages`
        );
        if (!res.ok) throw new Error("Failed to load messages");
        const data = await res.json();
        setMessages(data);
      } catch (err) {
        console.error(err);
      }
    }

    fetchMessages();
  }, [selectedConversation]);

  // Send a text message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("senderId", CURRENT_USER_ID);
      formData.append("type", "text");
      formData.append("content", newMessage.trim());

      const res = await fetch(
        `/api/conversations/${selectedConversation.id}/messages`,
        {
          method: "POST",
          body: formData
        }
      );

      if (!res.ok) throw new Error("Failed to send message");

      const message = await res.json();
      setMessages((prev) => [...prev, message]);
      setNewMessage("");

      // Update conversation in list
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedConversation.id
            ? { ...c, lastMessage: message, lastMessageAt: message.createdAt }
            : c
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  // Send media message
  const handleSendMedia = async (file) => {
    if (!file || !selectedConversation) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append("senderId", CURRENT_USER_ID);
      formData.append("media", file);

      const res = await fetch(
        `/api/conversations/${selectedConversation.id}/messages`,
        {
          method: "POST",
          body: formData
        }
      );

      if (!res.ok) throw new Error("Failed to send media");

      const message = await res.json();
      setMessages((prev) => [...prev, message]);

      // Update conversation
      setConversations((prev) =>
        prev.map((c) =>
          c.id === selectedConversation.id
            ? { ...c, lastMessage: message, lastMessageAt: message.createdAt }
            : c
        )
      );
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  // Start new conversation
  const handleStartConversation = async (participants, title) => {
    try {
      const res = await fetch(`/api/properties/${propertyId}/conversations`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "general",
          title,
          participants: [CURRENT_USER_ID, ...participants]
        })
      });

      if (!res.ok) throw new Error("Failed to create conversation");

      const conversation = await res.json();
      setConversations((prev) => [conversation, ...prev]);
      setSelectedConversation(conversation);
      setShowNewConversationModal(false);
    } catch (err) {
      console.error(err);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) {
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    return date.toLocaleDateString();
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const user = propertyUsers.find((pu) => pu.userId === userId);
    return user?.user?.name || "Unknown";
  };

  if (loading) {
    return (
      <div className="p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="grid grid-cols-3 gap-4 h-96">
          <div className="bg-gray-200 rounded"></div>
          <div className="col-span-2 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[600px] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-6 h-6 text-hn-red" />
          <h2 className="text-xl font-bold text-hn-dark">Messages</h2>
        </div>

        <button
          onClick={() => setShowNewConversationModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 transition"
        >
          <Plus className="w-4 h-4" />
          New Chat
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <div className="w-1/3 border-r overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-gray-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No conversations yet</p>
              <p className="text-sm">Start a chat with property users</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => setSelectedConversation(conv)}
                className={`w-full p-4 text-left border-b hover:bg-gray-50 transition ${
                  selectedConversation?.id === conv.id ? "bg-blue-50" : ""
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-hn-dark truncate">
                      {conv.title}
                    </p>
                    <p className="text-sm text-gray-500 truncate mt-1">
                      {conv.lastMessage?.type === "photo"
                        ? "Sent a photo"
                        : conv.lastMessage?.type === "video"
                        ? "Sent a video"
                        : conv.lastMessage?.type === "voice"
                        ? "Sent a voice note"
                        : conv.lastMessage?.content || "No messages yet"}
                    </p>
                  </div>
                  <div className="text-xs text-gray-400 ml-2">
                    {formatTime(conv.lastMessageAt)}
                  </div>
                </div>
                {conv.unreadCount > 0 && (
                  <span className="inline-block mt-2 px-2 py-0.5 bg-hn-red text-white text-xs rounded-full">
                    {conv.unreadCount}
                  </span>
                )}
              </button>
            ))
          )}
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {!selectedConversation ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Select a conversation to view messages</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="p-4 border-b bg-white">
                <h3 className="font-semibold text-hn-dark">
                  {selectedConversation.title}
                </h3>
                <p className="text-sm text-gray-500">
                  {selectedConversation.participants?.length || 0} participants
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {messages.map((msg) => {
                  const isOwn = msg.senderId === CURRENT_USER_ID;

                  return (
                    <div
                      key={msg.id}
                      className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                    >
                      <div
                        className={`max-w-[70%] ${
                          isOwn
                            ? "bg-hn-red text-white rounded-tl-xl rounded-tr-xl rounded-bl-xl"
                            : "bg-white border rounded-tl-xl rounded-tr-xl rounded-br-xl shadow-sm"
                        } p-3`}
                      >
                        {!isOwn && (
                          <p className="text-xs font-medium text-hn-red mb-1">
                            {getUserName(msg.senderId)}
                          </p>
                        )}

                        {/* Text content */}
                        {msg.type === "text" && (
                          <p className="text-sm">{msg.content}</p>
                        )}

                        {/* Photo */}
                        {msg.type === "photo" && msg.mediaUrl && (
                          <img
                            src={`${msg.mediaUrl}`}
                            alt="Shared"
                            className="rounded-lg max-w-full"
                          />
                        )}

                        {/* Video */}
                        {msg.type === "video" && msg.mediaUrl && (
                          <video
                            src={`${msg.mediaUrl}`}
                            controls
                            className="rounded-lg max-w-full"
                          />
                        )}

                        {/* Voice */}
                        {msg.type === "voice" && msg.mediaUrl && (
                          <audio
                            src={`${msg.mediaUrl}`}
                            controls
                            className="w-full"
                          />
                        )}

                        <div
                          className={`flex items-center gap-1 mt-1 text-xs ${
                            isOwn ? "text-red-200" : "text-gray-400"
                          }`}
                        >
                          <span>{formatTime(msg.createdAt)}</span>
                          {isOwn && (
                            msg.readBy?.length > 1 ? (
                              <CheckCheck className="w-3 h-3" />
                            ) : (
                              <Check className="w-3 h-3" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <form
                onSubmit={handleSendMessage}
                className="p-4 border-t bg-white"
              >
                <div className="flex items-center gap-2">
                  {/* Attachment button */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*,video/*,audio/*"
                    onChange={(e) => {
                      if (e.target.files?.[0]) {
                        handleSendMedia(e.target.files[0]);
                        e.target.value = "";
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-400 hover:text-hn-red transition"
                  >
                    <Paperclip className="w-5 h-5" />
                  </button>

                  {/* Text input */}
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 border rounded-full px-4 py-2 focus:ring-2 focus:ring-hn-red focus:border-transparent"
                  />

                  {/* Send button */}
                  <button
                    type="submit"
                    disabled={!newMessage.trim() || sending}
                    className="p-2 bg-hn-red text-white rounded-full hover:bg-red-700 disabled:bg-gray-300 transition"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>

      {/* New Conversation Modal */}
      {showNewConversationModal && (
        <NewConversationModal
          users={propertyUsers}
          onClose={() => setShowNewConversationModal(false)}
          onStart={handleStartConversation}
        />
      )}
    </div>
  );
}

// New Conversation Modal Component
function NewConversationModal({ users, onClose, onStart }) {
  const [title, setTitle] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedUsers.length === 0) return;
    onStart(selectedUsers, title || "New Conversation");
  };

  const toggleUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-bold text-hn-dark">New Conversation</h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Conversation Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-hn-red"
              placeholder="e.g., Kitchen Repair Discussion"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Participants
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {users
                .filter((pu) => pu.userId !== CURRENT_USER_ID)
                .map((pu) => (
                  <label
                    key={pu.userId}
                    className={`flex items-center p-3 rounded-lg border cursor-pointer transition ${
                      selectedUsers.includes(pu.userId)
                        ? "border-hn-red bg-red-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(pu.userId)}
                      onChange={() => toggleUser(pu.userId)}
                      className="sr-only"
                    />
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600 mr-3">
                      {pu.user?.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <p className="font-medium text-hn-dark">
                        {pu.user?.name || "Unknown"}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {pu.role?.replace("_", " ")}
                      </p>
                    </div>
                    {selectedUsers.includes(pu.userId) && (
                      <Check className="ml-auto w-5 h-5 text-hn-red" />
                    )}
                  </label>
                ))}
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={selectedUsers.length === 0}
              className="flex-1 px-4 py-2 bg-hn-red text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 transition"
            >
              Start Chat
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
